import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import {
    ArrowLeft,
    Copy,
    Download,
    FileText,
    Film,
    Image as ImageIcon,
    Library,
    Music2,
    Instagram,
    Facebook,
    Sparkles,
} from "lucide-react";
import { toast } from "sonner";

import TopNav from "@/components/smartpublish/TopNav";
import GradientOrb from "@/components/smartpublish/GradientOrb";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { api } from "@/lib/api";

const PLATFORM_META = {
    tiktok: {
        label: "TikTok",
        Icon: Music2,
        text: "text-[#FF2C55]",
        bg: "bg-[#FF2C55]/10",
        border: "border-[#FF2C55]/35",
        bar: "linear-gradient(180deg, rgba(255,44,85,0.9) 0%, rgba(0,245,212,0.6) 100%)",
    },
    instagram: {
        label: "Instagram",
        Icon: Instagram,
        text: "text-[#F77737]",
        bg: "bg-[#F77737]/10",
        border: "border-[#F77737]/35",
        bar: "linear-gradient(180deg, rgba(247,119,55,0.95) 0%, rgba(228,64,95,0.85) 60%, rgba(131,58,180,0.8) 100%)",
    },
    facebook: {
        label: "Facebook",
        Icon: Facebook,
        text: "text-[#5B8DEF]",
        bg: "bg-[#2F58CD]/15",
        border: "border-[#2F58CD]/40",
        bar: "linear-gradient(180deg, rgba(47,88,205,0.95) 0%, rgba(91,141,239,0.85) 100%)",
    },
};

const ASSET_ICON = {
    video: Film,
    image: ImageIcon,
    text: FileText,
};

function isFuture(iso) {
    return new Date(iso).getTime() > Date.now();
}

export default function Dashboard() {
    const [posts, setPosts] = useState(null); // null = loading
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/posts/scheduled");
                if (!cancelled) setPosts(Array.isArray(data) ? data : []);
            } catch (err) {
                if (cancelled) return;
                const detail =
                    err?.response?.data?.detail || err?.message || "Could not load dashboard";
                setError(String(detail));
                setPosts([]);
                toast.error("Dashboard failed to load", { description: String(detail) });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const { upcoming, past } = useMemo(() => {
        const u = [];
        const p = [];
        (posts || []).forEach((post) => {
            if (isFuture(post.scheduled_for)) u.push(post);
            else p.push(post);
        });
        // upcoming ascending (soonest first); past descending (most recent first)
        u.sort((a, b) => new Date(a.scheduled_for) - new Date(b.scheduled_for));
        p.sort((a, b) => new Date(b.scheduled_for) - new Date(a.scheduled_for));
        return { upcoming: u, past: p };
    }, [posts]);

    const loading = posts === null;
    const empty = !loading && upcoming.length === 0 && past.length === 0;

    return (
        <div className="relative min-h-screen overflow-x-hidden bg-[#0B0F19] text-[#E8ECF5]">
            <GradientOrb />
            <TopNav />

            <main className="relative z-10 mx-auto flex w-full max-w-[960px] flex-col gap-10 px-6 py-12 sm:py-16">
                <motion.section
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                    className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-6"
                >
                    <div className="flex flex-col gap-2">
                        <span
                            className="text-[10px] uppercase tracking-[0.32em] text-[#8A93A6]"
                            data-testid="dashboard-eyebrow"
                        >
                            phase 02 · library
                        </span>
                        <h1
                            className="text-4xl font-medium tracking-[-0.02em] text-[#E8ECF5] sm:text-5xl"
                            data-testid="dashboard-title"
                        >
                            Content Library
                        </h1>
                        <p
                            className="text-sm text-[#8A93A6]"
                            data-testid="dashboard-subtitle"
                        >
                            Everything you&apos;ve prepared from this session
                            — ready to copy and post manually.
                        </p>
                    </div>
                    <Link
                        to="/"
                        className="group inline-flex w-fit items-center gap-1.5 rounded-md border border-[#1F2A40] bg-[#0B0F19] px-3 py-1.5 text-xs text-[#8A93A6] transition-colors hover:border-[#00F5D4]/60 hover:text-[#00F5D4]"
                        data-testid="back-to-compose-btn"
                    >
                        <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-200 group-hover:-translate-x-0.5" />
                        Back to compose
                    </Link>
                </motion.section>

                {loading && <DashboardSkeleton />}

                {empty && !error && <EmptyState />}

                {!loading && !empty && (
                    <div
                        className="flex flex-col gap-8"
                        data-testid="dashboard-list"
                    >
                        {upcoming.length > 0 && (
                            <PostGroup
                                label="upcoming"
                                posts={upcoming}
                                testId="dashboard-upcoming"
                            />
                        )}
                        {past.length > 0 && (
                            <>
                                <div className="flex items-center gap-3">
                                    <span
                                        className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#8A93A6]"
                                        data-testid="past-divider-label"
                                    >
                                        past
                                    </span>
                                    <div className="h-px flex-1 bg-[#1F2A40]" />
                                </div>
                                <PostGroup
                                    label="past"
                                    posts={past}
                                    muted
                                    testId="dashboard-past"
                                />
                            </>
                        )}
                    </div>
                )}
            </main>

            <footer className="relative z-10 mx-auto w-full max-w-[960px] px-6 pb-10 pt-4">
                <p
                    className="font-mono text-xs tracking-wide text-[#8A93A6]/80"
                    data-testid="footer-text"
                >
                    SmartPublish · orchestrating content
                </p>
            </footer>
        </div>
    );
}

function PostGroup({ posts, muted = false, testId }) {
    return (
        <ul className="flex flex-col gap-3" data-testid={testId}>
            <AnimatePresence initial>
                {posts.map((post, idx) => (
                    <motion.li
                        key={post.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                            duration: 0.35,
                            delay: idx * 0.05,
                            ease: [0.25, 0.1, 0.25, 1],
                        }}
                        data-testid={`scheduled-post-${idx}`}
                    >
                        <PostRow post={post} muted={muted} />
                    </motion.li>
                ))}
            </AnimatePresence>
        </ul>
    );
}

function PostRow({ post, muted = false }) {
    const primaryPlatform = post.platforms?.[0] || "tiktok";
    const primaryMeta = PLATFORM_META[primaryPlatform] || PLATFORM_META.tiktok;
    const AssetIcon = ASSET_ICON[post.asset_kind] || FileText;
    const when = new Date(post.scheduled_for);
    const visibleHashtags = (post.hashtags || []).slice(0, 5);
    const overflow = Math.max(0, (post.hashtags || []).length - visibleHashtags.length);

    return (
        <div
            tabIndex={0}
            className={[
                "group relative flex flex-col gap-4 overflow-hidden rounded-2xl border border-[#1F2A40] bg-[#111726] p-5 transition-all duration-200 sm:flex-row sm:items-stretch sm:gap-5",
                "hover:-translate-y-[1px] hover:border-[#00F5D4]/40 hover:shadow-[0_8px_32px_-12px_rgba(0,245,212,0.18)] focus-visible:border-[#00F5D4]/60 focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_rgba(0,245,212,0.25)]",
                muted ? "opacity-70" : "",
            ].join(" ")}
            data-testid="scheduled-post-card"
        >
            {/* Accent bar tinted by primary platform */}
            <div
                aria-hidden
                className="absolute inset-y-0 left-0 w-[3px]"
                style={{ background: primaryMeta.bar }}
                data-testid="accent-bar"
                data-platform={primaryPlatform}
            />

            {/* Asset icon column */}
            <div className="flex shrink-0 items-start gap-3 pl-1 sm:flex-col sm:items-center sm:gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#1F2A40] bg-[#0B0F19] text-[#8A93A6] group-hover:text-[#00F5D4]">
                    <AssetIcon className="h-4 w-4" />
                </div>
                <span className="hidden font-mono text-[10px] uppercase tracking-[0.2em] text-[#8A93A6] sm:inline">
                    {post.asset_kind || "asset"}
                </span>
            </div>

            {/* Middle column — title, caption, hashtags */}
            <div className="flex min-w-0 flex-1 flex-col gap-2">
                <h3
                    className="text-base font-medium leading-snug tracking-[-0.005em] text-[#E8ECF5] sm:text-lg"
                    data-testid="post-title"
                >
                    {post.title}
                </h3>
                <p
                    className="line-clamp-1 text-sm text-[#8A93A6]"
                    data-testid="post-caption"
                >
                    {post.caption}
                </p>
                {visibleHashtags.length > 0 && (
                    <div
                        className="flex flex-wrap gap-1.5 pt-0.5"
                        data-testid="post-hashtags"
                    >
                        {visibleHashtags.map((h, i) => (
                            <span
                                key={`${h}-${i}`}
                                className="rounded-md border border-[#1F2A40] bg-[#0B0F19] px-2 py-0.5 font-mono text-[10px] text-[#8A93A6]"
                            >
                                {h}
                            </span>
                        ))}
                        {overflow > 0 && (
                            <span
                                className="rounded-md border border-[#1F2A40] bg-[#0B0F19] px-2 py-0.5 font-mono text-[10px] text-[#00F5D4]"
                                data-testid="post-hashtags-overflow"
                            >
                                +{overflow} more
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* Right column — when, platforms, status, actions */}
            <div className="flex shrink-0 flex-col items-start gap-3 border-t border-[#1F2A40]/60 pt-4 sm:items-end sm:border-l sm:border-t-0 sm:pl-5 sm:pt-0">
                <div className="flex flex-col gap-0.5">
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8A93A6]">
                        planned for
                    </span>
                    <span
                        className="text-sm font-medium text-[#E8ECF5]"
                        data-testid="post-day"
                    >
                        {format(when, "EEE d MMM")}
                    </span>
                    <span
                        className="font-mono text-xs text-[#8A93A6]"
                        data-testid="post-time"
                    >
                        {format(when, "h:mm a")}
                    </span>
                </div>

                <div
                    className="flex flex-wrap gap-1.5"
                    data-testid="post-platforms"
                >
                    {(post.platforms || []).map((p) => {
                        const meta = PLATFORM_META[p] || PLATFORM_META.tiktok;
                        return (
                            <span
                                key={p}
                                className={[
                                    "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium tracking-wide",
                                    meta.bg,
                                    meta.border,
                                    meta.text,
                                ].join(" ")}
                                data-platform={p}
                            >
                                <meta.Icon className="h-2.5 w-2.5" />
                                {meta.label}
                            </span>
                        );
                    })}
                </div>

                <span
                    className="inline-flex items-center gap-1 rounded-full border border-[#00F5D4]/40 bg-[#00F5D4]/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-[#00F5D4]"
                    data-testid="post-status"
                >
                    <span className="h-1.5 w-1.5 rounded-full bg-[#00F5D4]" />
                    {post.status || "ready"}
                </span>

                <RowActions post={post} />
            </div>
        </div>
    );
}

function RowActions({ post }) {
    const [downloading, setDownloading] = useState(false);
    const hashtagsLine = (post.hashtags || []).join(" ");
    const captionPayload = hashtagsLine
        ? `${post.caption}\n\n${hashtagsLine}`
        : post.caption;

    const copy = async (text, label, what) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`Copied for ${label}`, { description: what });
        } catch {
            toast.error("Copy failed", {
                description: "Long-press to copy manually.",
            });
        }
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const res = await api.get(`/assets/${post.asset_id}/download`, {
                responseType: "blob",
            });
            const cd = res.headers["content-disposition"] || "";
            const match = /filename="?([^";]+)"?/.exec(cd);
            const filename = match ? match[1] : "asset";
            const mime =
                res.headers["content-type"] || "application/octet-stream";
            const blob = new Blob([res.data], { type: mime });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            toast.success("Download started", { description: filename });
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                err?.message ||
                "Download failed";
            toast.error("Download failed", { description: String(detail) });
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="flex items-center gap-1.5" data-testid="row-actions">
            <Popover>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        className="inline-flex items-center gap-1.5 rounded-md border border-[#1F2A40] bg-[#0B0F19] px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-[#8A93A6] transition-colors hover:border-[#00F5D4]/40 hover:text-[#00F5D4]"
                        data-testid="row-copy-trigger"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Copy className="h-3 w-3" />
                        Copy
                    </button>
                </PopoverTrigger>
                <PopoverContent
                    side="bottom"
                    align="end"
                    className="z-50 w-[260px] border-[#1F2A40] bg-[#111726] p-3 text-[#E8ECF5]"
                    data-testid="row-copy-popover"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex flex-col gap-2">
                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8A93A6]">
                            copy variants
                        </span>
                        {(post.platforms || []).map((p) => {
                            const meta =
                                PLATFORM_META[p] || PLATFORM_META.tiktok;
                            return (
                                <button
                                    key={p}
                                    type="button"
                                    onClick={() =>
                                        copy(
                                            captionPayload,
                                            meta.label,
                                            "Caption & hashtags"
                                        )
                                    }
                                    className={[
                                        "inline-flex w-full items-center justify-between gap-2 rounded-md border bg-transparent px-2.5 py-1.5 text-xs transition-colors",
                                        meta.border,
                                        meta.text,
                                        "hover:bg-[#1A2235]",
                                    ].join(" ")}
                                    data-testid={`row-copy-caption-${p}`}
                                >
                                    <span className="inline-flex items-center gap-1.5">
                                        <meta.Icon className="h-3 w-3" />
                                        Caption · {meta.label}
                                    </span>
                                    <Copy className="h-3 w-3 opacity-60" />
                                </button>
                            );
                        })}
                        <button
                            type="button"
                            onClick={() =>
                                copy(post.title, "all platforms", "Title only")
                            }
                            className="mt-1 inline-flex w-full items-center justify-between gap-2 rounded-md border border-[#1F2A40] bg-[#0B0F19] px-2.5 py-1.5 text-xs text-[#8A93A6] transition-colors hover:border-[#00F5D4]/40 hover:text-[#00F5D4]"
                            data-testid="row-copy-title"
                        >
                            <span className="inline-flex items-center gap-1.5">
                                <Copy className="h-3 w-3" />
                                Title
                            </span>
                            <Copy className="h-3 w-3 opacity-60" />
                        </button>
                    </div>
                </PopoverContent>
            </Popover>

            <button
                type="button"
                onClick={handleDownload}
                disabled={downloading}
                className="inline-flex items-center gap-1.5 rounded-md border border-[#1F2A40] bg-[#0B0F19] px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-[#8A93A6] transition-colors hover:border-[#5B8DEF]/50 hover:text-[#5B8DEF] disabled:cursor-not-allowed disabled:opacity-40"
                data-testid="row-download-btn"
            >
                <Download className="h-3 w-3" />
                {downloading ? "…" : "Download"}
            </button>
        </div>
    );
}

function DashboardSkeleton() {
    return (
        <div
            className="flex flex-col gap-3"
            data-testid="dashboard-loading"
        >
            <div className="flex items-center gap-2">
                <motion.span
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                    className="h-2 w-2 rounded-full bg-[#00F5D4]"
                />
                <span className="font-mono text-xs uppercase tracking-[0.32em] text-[#00F5D4]">
                    Loading your queue…
                </span>
            </div>
            {[0, 1, 2].map((i) => (
                <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.1 }}
                    className="sp-shimmer relative h-28 overflow-hidden rounded-2xl border border-[#1F2A40] bg-[#111726]"
                >
                    <div className="flex h-full gap-4 p-5">
                        <div className="h-10 w-10 rounded-lg bg-[#1A2235]" />
                        <div className="flex flex-1 flex-col gap-2">
                            <div className="h-4 w-1/2 rounded-full bg-[#1A2235]" />
                            <div className="h-3 w-3/4 rounded-full bg-[#1A2235]" />
                            <div className="h-3 w-1/3 rounded-full bg-[#1A2235]" />
                        </div>
                        <div className="flex w-28 flex-col items-end gap-1.5">
                            <div className="h-3 w-16 rounded-full bg-[#1A2235]" />
                            <div className="h-4 w-20 rounded-full bg-[#1A2235]" />
                            <div className="h-3 w-12 rounded-full bg-[#1A2235]" />
                        </div>
                    </div>
                </motion.div>
            ))}
        </div>
    );
}

function EmptyState() {
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative mx-auto flex w-full max-w-md flex-col items-center gap-5 overflow-hidden rounded-2xl border border-[#1F2A40] bg-[#111726] p-10 text-center"
            data-testid="dashboard-empty"
        >
            <div
                aria-hidden
                className="absolute -top-16 left-1/2 h-44 w-44 -translate-x-1/2 rounded-full opacity-50 blur-2xl"
                style={{
                    background:
                        "radial-gradient(circle, rgba(0,245,212,0.25) 0%, rgba(47,88,205,0.15) 50%, transparent 75%)",
                }}
            />
            <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl border border-[#1F2A40] bg-[#0B0F19] text-[#00F5D4]">
                <Library className="h-6 w-6" />
            </div>
            <div className="relative flex flex-col gap-1.5">
                <h2 className="text-xl font-medium tracking-tight text-[#E8ECF5]">
                    No approved posts yet
                </h2>
                <p className="text-sm text-[#8A93A6]">
                    Compose something to add to your library.
                </p>
            </div>
            <Link
                to="/"
                className="group relative inline-flex items-center gap-2 overflow-hidden rounded-lg bg-gradient-to-r from-[#2F58CD] to-[#2F58CD] px-4 py-2 text-sm font-medium text-white transition-all hover:shadow-[0_0_24px_rgba(0,245,212,0.4)]"
                data-testid="empty-create-post-btn"
            >
                <span className="relative z-10 inline-flex items-center gap-2">
                    <Sparkles className="h-3.5 w-3.5" />
                    Open compose
                </span>
                <span
                    aria-hidden
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-[#00F5D4]/35 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                />
            </Link>
        </motion.div>
    );
}
